import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { FaCheckDouble } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'

import TextInput from '../../../shared/TextInput'
import DropdownField from '../../../shared/DropdownField'
import CustomButton from '../../../shared/CustomButton'
import { fetchData } from '../../../helpers/fetchData'
import { maxLengthValidation } from '../../../utils/MaxLengthValidation'
import { priceListTypesOptionsFormat } from '../../../constants/PriceListConstants'
import CheckPermission from '../../../hooks/CheckPermission'
import {
  useCreatePriceListSubTypeMutation,
  useUpdatePriceListSubTypeMutation
} from '../../../redux/api/modules/managementApi'

const CreatePriceSubTypeForm = ({
  selectedItemData,
  refetch,
  getPriceTypePayload,
  setOpenCreatePriceTypeModal,
  setSelectedItemData
}) => {
  const [createPriceListSubType, { isLoading: isCreating }] = useCreatePriceListSubTypeMutation()
  const [updatePriceListSubType, { isLoading: isUpdating }] = useUpdatePriceListSubTypeMutation()
  const [isLoading, setIsLoading] = useState(false)
  const [priceListTypes, setPriceListTypes] = useState([])
  const [fieldLoading, setFieldLoading] = useState(false)
  const { hasPermission } = CheckPermission()
  const ishaveGettypePermission = hasPermission('pricelist_types', 'get')

  let initialValues = {
    name_en: selectedItemData?.name_en || '',
    name_ar: selectedItemData?.name_ar || '',
    price_list_type_id: selectedItemData?.price_list_type_id || ''
  }
  const { t } = useTranslation()
  const validationSchema = Yup.object().shape({
    name_en: Yup.string()
      .trim()
      .concat(maxLengthValidation(40))
      .required(t('NameENisrequired')),
    name_ar: Yup.string()
      .trim()
      .concat(maxLengthValidation(40))
      .required(t('NameARisrequired')),
    price_list_type_id: Yup.string().required(
      t('select_price_list_type_required')
    )
  })

  const handleSubmit = async values => {
    const action = selectedItemData ? 'update' : 'create'
    if (!hasPermission('pricelist_subtypes', action)) return
    const selectedType = priceListTypes?.find(
      data => data?.id == values?.price_list_type_id
    )
    let payload = {
      ...values,
      price_list_type: selectedType
    }
    setIsLoading(true)
    if (selectedItemData) {
      hanldeUpdateSubItemType(payload)
    } else {
      handleCreateSubItemType(payload)
    }
  }

  const handleCreateSubItemType = async payload => {
    try {
      const res = await createPriceListSubType(payload).unwrap()
      if (res?.message) {
        toast.success(res?.message)
        setOpenCreatePriceTypeModal(false)
        if (typeof refetch === 'function') refetch('POST', '/v3/pricelist_subtypes/list', getPriceTypePayload)
      }
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'Failed to create price subtype')
    }
    setIsLoading(false)
  }

  const hanldeUpdateSubItemType = async payload => {
    payload.id = selectedItemData?.id
    try {
      const res = await updatePriceListSubType(payload).unwrap()
      if (res) {
        toast.success(res?.message)
        setOpenCreatePriceTypeModal(false)
        setSelectedItemData(null)
        if (typeof refetch === 'function') refetch('POST', '/v3/pricelist_subtypes/list', getPriceTypePayload)
      }
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'Failed to update price subtype')
    }
    setIsLoading(false)
  }

  const getPriceListTypes = async () => {
    if (!ishaveGettypePermission) return
    setFieldLoading(true)
    const response = await fetchData('POST', '/v3/pricelist_types/get', {})
    response?.data ? setPriceListTypes(response?.data) : setPriceListTypes([])
    setFieldLoading(false)
  }

  useEffect(() => {
    getPriceListTypes()
  }, [])

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {props => (
          <Form>
            <div className='grid grid-cols-2 gap-x-2 gap-y-1 mt-2'>
              <TextInput
                name='name_en'
                label={t('NameEN')}
                placeholder={t('Enterpriceitemtypename')}
                readOnly={!!selectedItemData}
              />
              <TextInput
                name='name_ar'
                label={t('NameAR')}
                placeholder={t('Enterpriceitemtypename')}
                readOnly={!!selectedItemData}
              />
              <DropdownField
                loading={fieldLoading}
                name='price_list_type_id'
                label={t('select_price_list_type')}
                placeholder={t('select_price_list_type')}
                options={priceListTypesOptionsFormat(priceListTypes)}
              />
            </div>
            <div className='flex items-center justify-end mt-3'>
              {hasPermission('pricelist_subtypes', selectedItemData ? 'update' : 'create') && <CustomButton
                disabled={isLoading || isCreating || isUpdating}
                type='submit'
                loading={isLoading || isCreating || isUpdating}
                icon={<FaCheckDouble size={12} className='me-2' />}
                label={
                  selectedItemData
                    ? t('update_price_item_sub_type')
                    : t('add_price_item_sub_type')
                }
                buttonType='primary'
              />}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default CreatePriceSubTypeForm
